import {
  formatAmount,
  LIVE_BREAK_MAX_PLAYLIST_BOXES,
  LIVE_BREAK_MAX_PLAYLISTS,
  LIVE_BREAK_PLAYLIST_NAME_MAX,
} from "@loot-os/api-contract";
import type {
  StaffPlaylistBoxWire,
  StaffPlaylistWire,
} from "@loot-os/functions/src/dashboard-api/trpc/routers/liveBreakWire.js";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@loot-os/ui/components/alert-dialog";
import { Badge } from "@loot-os/ui/components/badge";
import { BoxImage } from "@loot-os/ui/components/box-image";
import { Button } from "@loot-os/ui/components/button";
import { Card, CardContent } from "@loot-os/ui/components/card";
import { DataTable } from "@loot-os/ui/components/data-table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@loot-os/ui/components/dialog";
import { Field, FieldDescription, FieldLabel } from "@loot-os/ui/components/field";
import { Input } from "@loot-os/ui/components/input";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@loot-os/ui/components/sheet";
import { Skeleton } from "@loot-os/ui/components/skeleton";
import { toast } from "@loot-os/ui/components/sonner";
import { createDataTableColumns } from "@loot-os/ui/lib/dataTable";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, Plus, X } from "lucide-react";
import { useMemo, useState } from "react";

import { ErrorAlert } from "../components/ErrorAlert";
import { LiveBreakBoxPicker } from "../components/LiveBreakBoxPicker";
import { RowDetailButton } from "../components/RowDetailButton";
import { ShortDate } from "../components/ShortDate";
import { useCurrency } from "../lib/currency";
import { LIVE_BREAK_BOX_INELIGIBLE_LABELS, moveId } from "../lib/liveBreakStaff";
import { useTRPC } from "../lib/trpc";
import { useTenantRole } from "../lib/useTenantRole";

type Playlist = StaffPlaylistWire;

/** The boxes of a playlist a host's copy skips: gone from the environment, or unsellable now. */
function skippedCount(playlist: Playlist): number {
  return playlist.boxes.filter((box) => !box.isVisible || box.ineligibleReason !== null).length;
}

const playlistColumns = createDataTableColumns<Playlist>();
const PINNED = { start: [], end: ["actions"] };

function buildColumns(args: {
  canEdit: boolean;
  ids: readonly string[];
  reordering: boolean;
  onOpen: (id: string) => void;
  onMove: (id: string, by: -1 | 1) => void;
}) {
  const columns = [
    playlistColumns.accessor("position", {
      header: "Order",
      size: 112,
      cell: ({ row }) => {
        const index = args.ids.indexOf(row.original.id);
        return (
          <span className="flex items-center gap-1">
            <span className="w-6 text-right font-mono text-xs tabular-nums">{index + 1}</span>
            {args.canEdit && (
              <>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label={`Move ${row.original.name} up`}
                  disabled={args.reordering || index <= 0}
                  onClick={() => args.onMove(row.original.id, -1)}
                >
                  <ArrowUp />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label={`Move ${row.original.name} down`}
                  disabled={args.reordering || index >= args.ids.length - 1}
                  onClick={() => args.onMove(row.original.id, 1)}
                >
                  <ArrowDown />
                </Button>
              </>
            )}
          </span>
        );
      },
      meta: { csv: (value) => String(Number(value) + 1) },
    }),
    playlistColumns.accessor("name", {
      header: "Name",
      cell: ({ getValue, row }) => (
        <RowDetailButton onSelect={() => args.onOpen(row.original.id)}>
          <span className="font-medium hover:underline">{getValue()}</span>
        </RowDetailButton>
      ),
      meta: { filter: "text" },
    }),
    playlistColumns.accessor("slug", {
      header: "Slug",
      cell: ({ getValue }) => <span className="font-mono text-xs">{getValue()}</span>,
      meta: { filter: "text" },
    }),
    playlistColumns.accessor((playlist) => playlist.boxes.length, {
      id: "boxes",
      header: "Boxes",
      meta: { align: "end", filter: "number" },
    }),
    playlistColumns.accessor((playlist) => skippedCount(playlist), {
      id: "skipped",
      header: "Skipped by a copy",
      cell: ({ getValue }) =>
        getValue() > 0 ? (
          <Badge variant="outline">{getValue()}</Badge>
        ) : (
          <span className="text-muted-foreground">0</span>
        ),
      meta: { align: "end", filter: "number" },
    }),
    playlistColumns.accessor("updatedAt", {
      header: "Updated",
      cell: ({ getValue }) => <ShortDate value={getValue()} />,
    }),
  ];
  return playlistColumns.columns([
    ...columns,
    playlistColumns.display({
      id: "actions",
      size: args.canEdit ? 160 : 88,
      cell: ({ row }) => (
        <div className="flex justify-end gap-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => args.onOpen(row.original.id)}
          >
            {args.canEdit ? "Edit" : "View"}
          </Button>
          {args.canEdit && <DeletePlaylistButton playlist={row.original} />}
        </div>
      ),
    }),
  ]);
}

/**
 * Playlists (boxed's live-break playlists): named, ordered lists of boxes a host copies into a new
 * live break's boxes (the storefront's Suggested). A box a live break could not sell today is
 * skipped by the copy. Any member reads; admins create, rename, reorder, edit the boxes and
 * delete.
 */
export function LiveBreakPlaylistsPage() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { can } = useTenantRole();
  const canEdit = can("admin");
  const playlists = useQuery(trpc.liveBreaks.playlists.list.queryOptions());
  const [openId, setOpenId] = useState<string | null>(null);

  const rows = useMemo(() => playlists.data?.playlists ?? [], [playlists.data]);
  const ids = useMemo(() => rows.map((playlist) => playlist.id), [rows]);
  const reorder = useMutation(
    trpc.liveBreaks.playlists.reorder.mutationOptions({
      onSuccess: (data) => {
        queryClient.setQueryData(trpc.liveBreaks.playlists.list.queryKey(), data);
        toast.success("Playlist order saved.");
      },
      onSettled: () =>
        void queryClient.invalidateQueries({ queryKey: trpc.liveBreaks.playlists.pathKey() }),
    }),
  );
  const columns = useMemo(
    () =>
      buildColumns({
        canEdit,
        ids,
        reordering: reorder.isPending,
        onOpen: setOpenId,
        onMove: (id, by) => reorder.mutate({ playlistIds: moveId(ids, id, by) }),
      }),
    [canEdit, ids, reorder.isPending, reorder.mutate],
  );
  const open = rows.find((playlist) => playlist.id === openId) ?? null;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-2xl">
          <h1 className="text-2xl font-semibold tracking-tight">Playlists</h1>
          <p className="text-sm text-muted-foreground">
            Named lists of boxes a host copies into a new live break, shown to hosts in this order.
            A box a live break couldn't sell today is skipped by the copy. At most{" "}
            {LIVE_BREAK_MAX_PLAYLISTS} playlists, each of {LIVE_BREAK_MAX_PLAYLIST_BOXES} boxes.
          </p>
        </div>
        {canEdit && <CreatePlaylistDialog onCreated={setOpenId} />}
      </div>

      {playlists.error ? (
        <ErrorAlert message={playlists.error.message} />
      ) : (
        <Card>
          <CardContent>
            {playlists.isPending ? (
              <Skeleton className="h-40" />
            ) : (
              <DataTable
                columns={columns}
                data={rows}
                getRowId={(playlist) => playlist.id}
                pinned={PINNED}
                search="Search playlists"
                csvName="live-break-playlists"
                emptyMessage={
                  canEdit
                    ? "No playlists yet. Create one and add the boxes hosts should start from."
                    : "No playlists yet."
                }
              />
            )}
          </CardContent>
        </Card>
      )}

      <PlaylistSheet
        playlist={open}
        canEdit={canEdit}
        onOpenChange={(next) => {
          if (!next) setOpenId(null);
        }}
      />
    </div>
  );
}

/** Writes the answered playlist into the list's cache, then refetches the list. */
function usePlaylistWrite() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  return (playlist: Playlist) => {
    queryClient.setQueryData(trpc.liveBreaks.playlists.list.queryKey(), (old) =>
      old
        ? {
            playlists: old.playlists.map((row) => (row.id === playlist.id ? playlist : row)),
          }
        : old,
    );
    void queryClient.invalidateQueries({ queryKey: trpc.liveBreaks.playlists.pathKey() });
  };
}

/** A new playlist, last in order (`liveBreaks.playlists.create`, admins); opens it after. */
function CreatePlaylistDialog({ onCreated }: { onCreated: (id: string) => void }) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const create = useMutation(
    trpc.liveBreaks.playlists.create.mutationOptions({
      onSuccess: ({ playlist }) => {
        toast.success(`Created ${playlist.name}. Add its boxes next.`);
        void queryClient.invalidateQueries({ queryKey: trpc.liveBreaks.playlists.pathKey() });
        setOpen(false);
        setName("");
        onCreated(playlist.id);
      },
    }),
  );
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setName("");
      }}
    >
      <DialogTrigger render={<Button type="button" size="sm" />}>
        <Plus />
        New playlist
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim()) create.mutate({ name: name.trim() });
          }}
        >
          <DialogHeader>
            <DialogTitle>New playlist</DialogTitle>
            <DialogDescription>
              Its address on the storefront comes from the name now and stays when it is renamed.
            </DialogDescription>
          </DialogHeader>
          <PlaylistNameField id="playlist-new-name" value={name} onChange={setName} />
          <DialogFooter>
            <Button type="submit" disabled={!name.trim() || create.isPending}>
              {create.isPending ? "Creating…" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PlaylistNameField({
  id,
  value,
  disabled,
  onChange,
}: {
  id: string;
  value: string;
  disabled?: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <Field>
      <FieldLabel htmlFor={id}>Name</FieldLabel>
      <Input
        id={id}
        value={value}
        maxLength={LIVE_BREAK_PLAYLIST_NAME_MAX}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      />
      <FieldDescription>
        {`Up to ${LIVE_BREAK_PLAYLIST_NAME_MAX} characters, unique in this environment.`}
      </FieldDescription>
    </Field>
  );
}

/**
 * One playlist: its name (Rename) and its boxes in order, each movable and removable, with a box
 * picker over the environment's boxes. Every change saves on its own. Read-only below admin.
 */
function PlaylistSheet({
  playlist,
  canEdit,
  onOpenChange,
}: {
  playlist: Playlist | null;
  canEdit: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Sheet open={playlist !== null} onOpenChange={onOpenChange}>
      <SheetContent className="gap-0 overflow-y-auto data-[side=right]:w-full data-[side=right]:sm:max-w-xl">
        {playlist && <PlaylistEditor key={playlist.id} playlist={playlist} canEdit={canEdit} />}
      </SheetContent>
    </Sheet>
  );
}

function PlaylistEditor({ playlist, canEdit }: { playlist: Playlist; canEdit: boolean }) {
  const trpc = useTRPC();
  const write = usePlaylistWrite();
  const [name, setName] = useState(playlist.name);
  const rename = useMutation(
    trpc.liveBreaks.playlists.rename.mutationOptions({
      onSuccess: ({ playlist: saved }) => {
        write(saved);
        setName(saved.name);
        toast.success(`Renamed to ${saved.name}.`);
      },
    }),
  );
  const addBox = useMutation(
    trpc.liveBreaks.playlists.addBox.mutationOptions({
      onSuccess: ({ playlist: saved }) => {
        write(saved);
        toast.success(`Added to ${saved.name}.`);
      },
    }),
  );
  const removeBox = useMutation(
    trpc.liveBreaks.playlists.removeBox.mutationOptions({
      onSuccess: ({ playlist: saved }) => {
        write(saved);
        toast.success(`Removed from ${saved.name}.`);
      },
    }),
  );
  const setBoxes = useMutation(
    trpc.liveBreaks.playlists.setBoxes.mutationOptions({
      onSuccess: ({ playlist: saved }) => {
        write(saved);
        toast.success(`${saved.name}'s order saved.`);
      },
    }),
  );
  const boxIds = playlist.boxes.map((box) => box.boxId);
  const busy = addBox.isPending || removeBox.isPending || setBoxes.isPending;
  const full = playlist.boxes.length >= LIVE_BREAK_MAX_PLAYLIST_BOXES;

  return (
    <>
      <SheetHeader className="border-b pr-12">
        <SheetTitle className="text-base">{playlist.name}</SheetTitle>
        <SheetDescription>
          <span className="font-mono text-xs">{playlist.slug}</span> ·{" "}
          {`${playlist.boxes.length} of ${LIVE_BREAK_MAX_PLAYLIST_BOXES} boxes`}
        </SheetDescription>
      </SheetHeader>
      <div className="grid gap-6 p-4">
        {canEdit && (
          <form
            className="grid gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (name.trim() && name.trim() !== playlist.name) {
                rename.mutate({ playlistId: playlist.id, name: name.trim() });
              }
            }}
          >
            <PlaylistNameField
              id={`playlist-${playlist.id}-name`}
              value={name}
              disabled={rename.isPending}
              onChange={setName}
            />
            <div>
              <Button
                type="submit"
                size="sm"
                variant="outline"
                disabled={!name.trim() || name.trim() === playlist.name || rename.isPending}
              >
                {rename.isPending ? "Renaming…" : "Rename"}
              </Button>
            </div>
          </form>
        )}

        <div className="grid gap-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-medium">Boxes</h3>
            {canEdit && (
              <div className="w-full max-w-64 sm:w-64">
                <LiveBreakBoxPicker
                  excludeIds={new Set(boxIds)}
                  disabled={busy || full}
                  triggerLabel={full ? "The playlist is full" : "Add a box…"}
                  onPick={(box) => addBox.mutate({ playlistId: playlist.id, boxId: box.id })}
                />
              </div>
            )}
          </div>
          {playlist.boxes.length === 0 ? (
            <p className="rounded-md border px-3 py-6 text-center text-sm text-muted-foreground">
              {canEdit ? "No boxes yet. Add the first from the picker." : "No boxes yet."}
            </p>
          ) : (
            <ol className="divide-y rounded-md border">
              {playlist.boxes.map((box, index) => (
                <PlaylistBoxRow
                  key={box.boxId}
                  box={box}
                  index={index}
                  last={index === playlist.boxes.length - 1}
                  canEdit={canEdit}
                  busy={busy}
                  onMove={(by) =>
                    setBoxes.mutate({
                      playlistId: playlist.id,
                      boxIds: moveId(boxIds, box.boxId, by),
                    })
                  }
                  onRemove={() => removeBox.mutate({ playlistId: playlist.id, boxId: box.boxId })}
                />
              ))}
            </ol>
          )}
        </div>
      </div>
    </>
  );
}

function PlaylistBoxRow({
  box,
  index,
  last,
  canEdit,
  busy,
  onMove,
  onRemove,
}: {
  box: StaffPlaylistBoxWire;
  index: number;
  last: boolean;
  canEdit: boolean;
  busy: boolean;
  onMove: (by: -1 | 1) => void;
  onRemove: () => void;
}) {
  const currency = useCurrency();
  const name = box.name ?? "A box this environment no longer sees";
  return (
    <li className="flex items-center gap-3 px-3 py-2">
      <span className="w-5 shrink-0 text-right font-mono text-xs text-muted-foreground tabular-nums">
        {index + 1}
      </span>
      <BoxImage
        src={box.imageUrl ?? ""}
        className="size-10 shrink-0 rounded border object-contain"
      />
      <div className="grid min-w-0 flex-1 gap-0.5">
        <span
          className={`truncate text-sm ${box.isVisible ? "font-medium" : "text-muted-foreground"}`}
        >
          {name}
        </span>
        <span className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          {box.boxPriceCents !== null && (
            <span className="font-mono">{formatAmount(box.boxPriceCents, currency)}</span>
          )}
          {!box.isVisible ? (
            <Badge variant="outline">Gone</Badge>
          ) : (
            box.ineligibleReason && (
              <Badge variant="outline">
                {LIVE_BREAK_BOX_INELIGIBLE_LABELS[box.ineligibleReason]}
              </Badge>
            )
          )}
        </span>
      </div>
      {canEdit && (
        <div className="flex shrink-0 items-center">
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={`Move ${name} up`}
            disabled={busy || index === 0}
            onClick={() => onMove(-1)}
          >
            <ArrowUp />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={`Move ${name} down`}
            disabled={busy || last}
            onClick={() => onMove(1)}
          >
            <ArrowDown />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={`Remove ${name}`}
            disabled={busy}
            onClick={onRemove}
          >
            <X />
          </Button>
        </div>
      )}
    </li>
  );
}

/** Deletes a playlist after a confirmation; live breaks already copied from it keep their boxes. */
function DeletePlaylistButton({ playlist }: { playlist: Playlist }) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const remove = useMutation(
    trpc.liveBreaks.playlists.delete.mutationOptions({
      onSuccess: () => {
        toast.success(`Deleted ${playlist.name}.`);
        void queryClient.invalidateQueries({ queryKey: trpc.liveBreaks.playlists.pathKey() });
        setOpen(false);
      },
    }),
  );
  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger render={<Button type="button" variant="ghost" size="sm" />}>
        Delete
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{`Delete ${playlist.name}?`}</AlertDialogTitle>
          <AlertDialogDescription>
            Hosts stop seeing it. Live breaks already copied from it keep their boxes.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            disabled={remove.isPending}
            onClick={() => remove.mutate({ playlistId: playlist.id })}
          >
            {remove.isPending ? "Deleting…" : "Delete"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
